import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dumbbell, Calendar, Check } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTranslation } from "react-i18next";
import { Analytics } from "@/lib/analytics";

interface WorkoutProgramData {
  type: string;
  title: string;
  description: string;
  conversationId?: string;
  days: Array<{
    day?: string;
    workout?: string;
    workout_name?: string;
    exercises: Array<{
      exercise_name: string;
      sets?: number;
      reps?: number;
      weight_kg?: number;
    }>;
  }>;
}

interface WorkoutProgramImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  programData: WorkoutProgramData | null;
  onImport: () => void;
  isImporting: boolean;
}

export const WorkoutProgramReviewModal = ({
  isOpen,
  onClose,
  programData,
  onImport,
  isImporting
}: WorkoutProgramImportModalProps) => {
  const { t } = useTranslation();
  if (!programData) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            {t('components.workoutProgramReview.title')}
          </DialogTitle>
          <DialogDescription>
            {t('components.workoutProgramReview.description')}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[55vh] pr-4">
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-1">{programData.title}</h3>
              <p className="text-sm text-muted-foreground">{programData.description}</p>
            </div>

            <div className="space-y-3">
              {programData.days.map((day, dayIndex) => (
                <Card key={dayIndex} className="border-primary/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Dumbbell className="h-4 w-4 text-primary" />
                      <h4 className="font-semibold">
                        {t('components.workoutProgramReview.dayLabel', {
                          number: dayIndex + 1,
                          name: day.workout || day.workout_name || day.day || t('components.workoutProgramReview.workoutFallback', { number: dayIndex + 1 }),
                        })}
                      </h4>
                    </div>

                    {day.exercises && day.exercises.length > 0 ? (
                      <ul className="space-y-2 text-sm">
                        {day.exercises.map((exercise, exIndex) => (
                          <li key={exIndex} className="flex items-start gap-2">
                            <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>
                              <strong>{exercise.exercise_name}</strong>
                              {exercise.sets && <span>{t('components.workoutProgramReview.exerciseSets', { count: exercise.sets })}</span>}
                              {exercise.reps && <span>{t('components.workoutProgramReview.exerciseReps', { count: exercise.reps })}</span>}
                              {exercise.weight_kg && <span>{t('components.workoutProgramReview.exerciseWeight', { weight: exercise.weight_kg })}</span>}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground">{t('components.workoutProgramReview.restDay')}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </ScrollArea>

        <div className="flex gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => {
              Analytics.arnoldSuggestionDismissed('workout_program');
              onClose();
            }}
            disabled={isImporting}
            className="flex-1"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={onImport}
            disabled={isImporting}
            className="flex-1 bg-gradient-primary text-black"
          >
            {isImporting ? t('components.workoutProgramReview.importing') : t('components.workoutProgramReview.importProgram')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
